<script lang="ts">
  import {
    orgBurgerMenu,
    orgMagnifier,
    orgTime,
    orgExternalLink,
    orgFilterAlt,
    orgHouse,
    orgFolder,
  } from "@nrk/origo";
  import { productFruitsInstance } from "services/productFruitsService";
  import {
    getAllImagesLink,
    isAllImagesPage,
    isLandingPage,
    isJobsPage,
    pageState,
    isCollectionsPage,
  } from "state/page";
  import { uploads } from "state/upload";
  import { getMountElement } from "config";
  import { onMount } from "svelte";
  import { pageToUrl } from "state/url-mappers";
  import { userBelongsToStillsGroup } from "auth/auth";
  import { useFeatureToggle } from "services/configCatService";

  $: page = $pageState;
  $: uploadsInProgress = $uploads.state !== "IDLE";

  $: allImagesLink = getAllImagesLink($pageState);

  let isDarkTheme = true;

  onMount(() => {
    const root = getMountElement();
    if (!root?.classList.contains("bb-theme-dark")) {
      isDarkTheme = false;
    }
  });

  function setColorTheme(event: Event) {
    const target = event.target as HTMLInputElement;
    const root = getMountElement();
    if (!root) return;
    if (target.checked) {
      root.classList.add("bb-theme-dark");
      root.classList.remove("bb-theme-light");
      isDarkTheme = true;
    } else {
      root.classList.add("bb-theme-light");
      root.classList.remove("bb-theme-dark");
      isDarkTheme = false;
    }
  }

  let dropdown: HTMLElement;

  function closeDropdown() {
    dropdown.hidden = true;
  }
</script>

<button class="org-button non-active-state" aria-label="Åpne navigasjonsmeny">
  {@html orgBurgerMenu}
</button>

<bb-dropdown bind:this={dropdown} class="dropdown-menu" hidden>
  <div class="navigation-menu">
    <ul>
      <li>
        <a
          href={pageToUrl({ type: "LANDING_PAGE" }).href}
          aria-disabled={uploadsInProgress}
          class="org-button"
          on:click={() => {
            closeDropdown();
          }}
          aria-current={isLandingPage(page) ? "page" : undefined}
        >
          {@html orgHouse} Min side
        </a>
      </li>
      <li>
        <a
          href={allImagesLink.toString()}
          aria-disabled={uploadsInProgress}
          class="org-button"
          on:click={closeDropdown}
          aria-current={isAllImagesPage(page) ? "page" : undefined}
        >
          {@html orgMagnifier} Alle bilder
        </a>
      </li>
      {#if userBelongsToStillsGroup()}
        <li>
          <a
            href={pageToUrl({ type: "JOBS" }).href}
            aria-disabled={uploadsInProgress}
            class="org-button"
            aria-current={isJobsPage(page) ? "page" : undefined}
            on:click={() => {
              closeDropdown();
            }}
          >
            {@html orgTime} Oppdrag
          </a>
        </li>
      {/if}
      <li>
        <a
          href={pageToUrl({ type: "COLLECTIONS" }).href}
          aria-disabled={uploadsInProgress}
          class="org-button"
          aria-current={isCollectionsPage(page) ? "page" : undefined}
          on:click={() => {
                closeDropdown();
              }}
        >
          {@html orgFolder} Samlinger
        </a>
      </li>
      <hr class="org-hr"/>
      <li>
        <a
          href="https://nrk.slack.com/archives/C07LE98MG9K"
          rel="noopener noreferrer"
          target="_blank"
          class="org-button"
          on:click={closeDropdown}
        >
          Bildebanken på Slack {@html orgExternalLink}
        </a>
      </li>
      <li>
        <a
          href="https://portalen.nrk.no/bildebanken-hjelp/"
          class="org-button"
          target="_blank"
          rel="noopener noreferrer"
          on:click={closeDropdown}
        >
          Bildebanken på Portalen {@html orgExternalLink}
        </a>
      </li>
      <hr class="org-hr"/>
      <li>
        <button
          class="org-button feedback-button"
          disabled={!$productFruitsInstance}
          on:click|preventDefault={() => {
            $productFruitsInstance?.api.feedback.showModal();
            closeDropdown();
          }}
        >
          Gi oss din tilbakemelding!
        </button>
      </li>
      <li>
        <div class="theme-select">
          <span>Mørkt tema {@html orgFilterAlt}</span>
          <input
            class="org-switch"
            type="checkbox"
            aria-label="Sett mørkt tema"
            checked={isDarkTheme}
            on:change={(event) => {
              setColorTheme(event);
            }}
          />
        </div>
      </li>
    </ul>
  </div>
</bb-dropdown>

<style>
  .org-hr {
    margin: var(--org-small);
  }

  bb-dropdown {
    width: 300px;
    background-color: var(--color-surface-secondary);
    color: var(--color-text-primary);
    padding: var(--org-xsmall);
  }

  .navigation-menu {
    padding: var(--org-small);
    position: relative;
    background-color: var(--color-surface-secondary);
    color: var(--color-text-primary);
  }

  .non-active-state {
    box-shadow: none !important;
  }

  .theme-select {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 0.75rem;
  }

  .theme-select input {
    font-size: var(--org-small);
  }

  ul {
    padding-left: unset;
    margin-top: var(--org-xsmall);
    margin-bottom: var(--org-xsmall);
  }

  li {
    list-style: none;
  }

  .feedback-button {
    width: 100%;
    text-align: left;
    margin: 0;
  }

  hr {
    margin-top: var(--org-small);
    margin-bottom: 0;
  }

  li {
    margin-block: var(--org-xsmall);
  }

  a {
    width: 100%;
    text-align: left;
    margin: 0;
  }
</style>
